import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { ASSESSMENT_DRAFT_KEY, LEGACY_ASSESSMENT_DRAFT_KEY, createBlankDraft, parseAssessmentDraft, readAssessmentDraft, writeAssessmentDraft, type AssessmentDraft } from '../../lib/assessment-draft';
import { useAuth } from '../../lib/auth';
import { invalidateProfileReuse, type ProfileReuseConfirmations } from '../../lib/profile-reuse';
import { normalizeLanguageProfile, type AssessmentState } from '../../lib/crs';
import type { PathwayAnswers } from '../../lib/pathways';
import { normalizeEducationProfile } from '../../lib/education-dependencies';

const handoffKey = 'halo-assessment-handoff-v1';
type LoginHandoff = { time: number; raw: string };
function reconcileDraft(draft: AssessmentDraft): AssessmentDraft {
  const profile = normalizeLanguageProfile(normalizeEducationProfile(draft.profile));
  const valid = invalidateProfileReuse(profile, draft.answers, draft.reuseConfirmations);
  return { ...draft, profile, answers: valid.answers, reuseConfirmations: valid.confirmations };
}
function restoreDraft(key = ASSESSMENT_DRAFT_KEY): AssessmentDraft {
  try { return reconcileDraft(readAssessmentDraft(window.localStorage, key)); }
  catch { return createBlankDraft(); }
}
function hasStoredDraft(key: string): boolean {
  try {
    return localStorage.getItem(key) !== null || localStorage.getItem(LEGACY_ASSESSMENT_DRAFT_KEY + key.slice(ASSESSMENT_DRAFT_KEY.length)) !== null;
  } catch { return false; }
}
function readHandoff(value: unknown): AssessmentDraft | null {
  if (!value || typeof value !== 'object') return null;
  const handoff = value as Partial<LoginHandoff>;
  if (typeof handoff.time !== 'number' || !Number.isFinite(handoff.time) || Date.now() - handoff.time > 30 * 60 * 1000 || handoff.time > Date.now() + 30000 || typeof handoff.raw !== 'string') return null;
  try {
    const raw = JSON.parse(handoff.raw);
    if (!raw || raw.version !== 1 || !raw.profile || typeof raw.profile !== 'object' || Array.isArray(raw.profile) || !raw.answers || typeof raw.answers !== 'object' || Array.isArray(raw.answers)) return null;
    const draft = reconcileDraft(parseAssessmentDraft(handoff.raw));
    return JSON.stringify(draft) === JSON.stringify(createBlankDraft()) ? null : draft;
  } catch { return null; }
}
function takeHandoff(): AssessmentDraft | null {
  try {
    const raw = sessionStorage.getItem(handoffKey); sessionStorage.removeItem(handoffKey);
    if (!raw) return null;
    return readHandoff(JSON.parse(raw));
  } catch { return null; }
}
export function useAssessmentDraft() {
  const auth = useAuth(), owner = auth.user?.id ?? null;
  const key = owner ? `${ASSESSMENT_DRAFT_KEY}:${owner}` : ASSESSMENT_DRAFT_KEY;
  const [loadedKey, setLoadedKey] = useState<string | null>(null), [draft, setDraft] = useState(() => restoreDraft(key));
  const [resetBackup, setResetBackup] = useState<AssessmentDraft | null>(null);
  const memoryHandoff = useRef<LoginHandoff | null>(null), resolvedKey = useRef<string | null>(null), ownerRef = useRef(owner); ownerRef.current = owner;
  const savedDraft = useRef<{ key: string; raw: string; saved: boolean } | null>(null);
  const [storageStatus, setStorageStatus] = useState<'saved' | 'unavailable'>('unavailable');
  useEffect(() => {
    if (auth.loading || loadedKey === key || resolvedKey.current === key) return;
    resolvedKey.current = key;
    const account = restoreDraft(key);
    const handoff = (loadedKey === null || loadedKey === ASSESSMENT_DRAFT_KEY) && owner ? takeHandoff() ?? readHandoff(memoryHandoff.current) : null;
    memoryHandoff.current = null;
    const existing = hasStoredDraft(key);
    savedDraft.current = { key, raw: JSON.stringify(account), saved: existing };
    if (handoff && owner) {
      savedDraft.current = null; // An explicit continuation commits its own current-version snapshot.
      setDraft(handoff); setResetBackup(existing ? account : null);
      try { localStorage.removeItem(ASSESSMENT_DRAFT_KEY); localStorage.removeItem(LEGACY_ASSESSMENT_DRAFT_KEY); } catch { /* optional storage */ }
    } else { setDraft(account); setResetBackup(null); }
    setStorageStatus(existing ? 'saved' : 'unavailable'); setLoadedKey(key);
  }, [key, loadedKey, owner, auth.loading]);
  useEffect(() => {
    const clear = () => {
      const blank = createBlankDraft(), currentKey = ownerRef.current ? `${ASSESSMENT_DRAFT_KEY}:${ownerRef.current}` : ASSESSMENT_DRAFT_KEY;
      memoryHandoff.current = null; resolvedKey.current = null;
      savedDraft.current = { key: currentKey, raw: JSON.stringify(blank), saved: false };
      setResetBackup(null); setStorageStatus('unavailable'); setDraft(blank); setLoadedKey(currentKey);
    };
    const sync = (event: StorageEvent) => {
      if (event.key !== key) return;
      const restored = restoreDraft(key), saved = hasStoredDraft(key);
      savedDraft.current = { key, raw: JSON.stringify(restored), saved };
      setDraft(restored); setResetBackup(null); setStorageStatus(saved ? 'saved' : 'unavailable');
    };
    window.addEventListener('halo:private-data-cleared', clear); window.addEventListener('storage', sync);
    return () => { window.removeEventListener('halo:private-data-cleared', clear); window.removeEventListener('storage', sync); };
  }, [key]);
  const keepForLogin = useCallback(() => {
    if (owner || auth.loading || loadedKey !== key) return;
    const handoff = { time: Date.now(), raw: JSON.stringify(draft) };
    memoryHandoff.current = readHandoff(handoff) ? handoff : null;
    try {
      if (memoryHandoff.current) sessionStorage.setItem(handoffKey, JSON.stringify(handoff));
      else sessionStorage.removeItem(handoffKey);
    } catch { /* Same-tab memory survives blocked storage. */ }
  }, [draft, owner, auth.loading, loadedKey, key]);
  const setState: Dispatch<SetStateAction<AssessmentState>> = useCallback(next => {
    setResetBackup(null); setDraft(current => reconcileDraft({ ...current, profile: typeof next === 'function' ? next(current.profile) : next }));
  }, []);
  const setPathwayAnswers: Dispatch<SetStateAction<PathwayAnswers>> = useCallback(next => {
    setResetBackup(null); setDraft(current => reconcileDraft({ ...current, answers: typeof next === 'function' ? next(current.answers) : next }));
  }, []);
  const setReuseConfirmations: Dispatch<SetStateAction<ProfileReuseConfirmations>> = useCallback(next => {
    setDraft(current => reconcileDraft({ ...current, reuseConfirmations: typeof next === 'function' ? next(current.reuseConfirmations) : next }));
  }, []);
  const saveDraft = useCallback(() => {
    if (auth.loading || loadedKey !== key) return false;
    const raw = JSON.stringify(draft);
    // Restoring an account or a storage event is a read. An inactive tab must
    // not write its older render over a handoff adopted in another tab.
    if (savedDraft.current?.key === key && savedDraft.current.raw === raw) return savedDraft.current.saved;
    let saved = false;
    try { saved = writeAssessmentDraft(window.localStorage, draft, key); } catch { /* In-memory assessment still works. */ }
    if (saved) savedDraft.current = { key, raw, saved: true };
    setStorageStatus(saved ? 'saved' : 'unavailable'); return saved;
  }, [draft, key, loadedKey, auth.loading]);
  useEffect(() => { saveDraft(); }, [saveDraft]);
  const resetDraft = useCallback(() => { setResetBackup(draft); setDraft(createBlankDraft()); }, [draft]);
  const undoReset = useCallback(() => { if (resetBackup) { setDraft(reconcileDraft(resetBackup)); setResetBackup(null); } }, [resetBackup]);
  const ready = !auth.loading && loadedKey === key;
  const visibleDraft = ready ? draft : createBlankDraft();
  return { state: visibleDraft.profile, setState, pathwayAnswers: visibleDraft.answers, setPathwayAnswers, reuseConfirmations: visibleDraft.reuseConfirmations, setReuseConfirmations, saveDraft, storageStatus: ready ? storageStatus : 'unavailable' as const, resetDraft, undoReset, keepForLogin, ready, canUndoReset: ready && resetBackup !== null };
}
